import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, ListItemIcon, ListItemText, Menu, MenuItem, TextField } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, errorMessage } from "../api/client";

export type ViewConfig = { params?: Record<string, unknown>; columns?: Record<string, boolean> };
type SavedView = { id: string; name: string; is_shared: boolean; is_default: boolean; is_mine: boolean; config: ViewConfig };

export function SavedViewsMenu({ viewKey, current, onApply }: { viewKey: string; current: () => ViewConfig; onApply: (c: ViewConfig) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const views = useQuery({
    queryKey: ["saved-views", viewKey],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/saved-views/", { params: { query: { view_key: viewKey, page_size: 100 } } });
      if (error) throw new Error(errorMessage(error));
      return (data?.results ?? []) as unknown as SavedView[];
    },
  });
  const create = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST("/api/saved-views/", { body: { view_key: viewKey, name, is_shared: shared, config: current() } as never });
      if (error) throw new Error(errorMessage(error));
    },
    onSuccess: () => { setSaving(false); setName(""); void qc.invalidateQueries({ queryKey: ["saved-views", viewKey] }); },
  });
  const remove = useMutation({
    mutationFn: async (id: string) => { await api.DELETE("/api/saved-views/{id}/", { params: { path: { id } } }); },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["saved-views", viewKey] }),
  });
  return (
    <>
      <Button startIcon={<BookmarkBorderIcon />} onClick={(e) => setAnchor(e.currentTarget)}>{t("common.views")}</Button>
      <Menu open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)}>
        {(views.data ?? []).map((v) => (
          <MenuItem key={v.id} onClick={() => { onApply(v.config); setAnchor(null); }}>
            <ListItemText primary={v.name} secondary={v.is_shared ? "Shared" : undefined} />
            {v.is_mine && (
              <ListItemIcon sx={{ ml: 2 }} onClick={(e) => { e.stopPropagation(); remove.mutate(v.id); }}>
                <span aria-label="Delete view" style={{ fontSize: 12, cursor: "pointer" }}>✕</span>
              </ListItemIcon>
            )}
          </MenuItem>
        ))}
        {(views.data?.length ?? 0) > 0 && <Divider />}
        <MenuItem onClick={() => { setAnchor(null); setSaving(true); }}>{t("common.saveView")}…</MenuItem>
      </Menu>
      <Dialog open={saving} onClose={() => setSaving(false)}>
        <DialogTitle>{t("common.saveView")}</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 1 }}>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          <FormControlLabel control={<Checkbox checked={shared} onChange={(e) => setShared(e.target.checked)} />} label="Share with the organization" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaving(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" disabled={!name || create.isPending} onClick={() => create.mutate()}>{t("common.save")}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
